import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { authService } from "../services/authService";
import { useAuth } from "../contexts/AuthContext";
import { VerificationForm } from "./VerificationForm";
import { collectDeviceFingerprint } from "../utils/deviceFingerprint";
import "./Auth.css";

interface RegisterFormProps {
  showHeader?: boolean;
  onLoadingChange?: (loading: boolean) => void;
}

export const RegisterForm: React.FC<RegisterFormProps> = ({
  showHeader = true,
  onLoadingChange,
}) => {
  const { register, needsVerification, pendingRegistration } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromExtension = searchParams.get("from") === "extension";
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [stripeConsent, setStripeConsent] = useState(false);
  const firstNameRef = useRef<HTMLInputElement>(null);
  const lastNameRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const confirmPasswordRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const syncAutofilledValues = () => {
      const firstNameValue = firstNameRef.current?.value ?? "";
      const lastNameValue = lastNameRef.current?.value ?? "";
      const emailValue = emailRef.current?.value ?? "";
      const passwordValue = passwordRef.current?.value ?? "";
      const confirmPasswordValue = confirmPasswordRef.current?.value ?? "";

      if (firstNameValue && firstNameValue !== firstName) {
        setFirstName(firstNameValue);
      }
      if (lastNameValue && lastNameValue !== lastName) {
        setLastName(lastNameValue);
      }
      if (emailValue && emailValue !== email) {
        setEmail(emailValue);
      }
      if (passwordValue && passwordValue !== password) {
        setPassword(passwordValue);
      }
      if (confirmPasswordValue && confirmPasswordValue !== confirmPassword) {
        setConfirmPassword(confirmPasswordValue);
      }
    };

    syncAutofilledValues();
    const timeoutIds = [
      setTimeout(syncAutofilledValues, 50),
      setTimeout(syncAutofilledValues, 100),
      setTimeout(syncAutofilledValues, 250),
      setTimeout(syncAutofilledValues, 500),
      setTimeout(syncAutofilledValues, 1000),
    ];
    const intervalId = setInterval(syncAutofilledValues, 500);

    const handleInput = () => {
      syncAutofilledValues();
    };

    const fields = [
      firstNameRef.current,
      lastNameRef.current,
      emailRef.current,
      passwordRef.current,
      confirmPasswordRef.current,
    ];

    fields.forEach((field) => {
      field?.addEventListener("input", handleInput);
      field?.addEventListener("change", handleInput);
    });

    return () => {
      timeoutIds.forEach((id) => clearTimeout(id));
      clearInterval(intervalId);
      fields.forEach((field) => {
        field?.removeEventListener("input", handleInput);
        field?.removeEventListener("change", handleInput);
      });
    };
  }, [confirmPassword, email, firstName, lastName, password]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const firstName = formData.get("firstName") as string;
    const lastName = formData.get("lastName") as string;
    const email = formData.get("email") as string;
    const password = formData.get("password") as string;
    const confirmPassword = formData.get("confirmPassword") as string;

    // Validate email format
    if (!authService.isValidEmail(email)) {
      toast.error("Please enter a valid email address.", {
        duration: 5000,
        position: "top-right",
        style: {
          background: "#dc2626",
          color: "#fff",
          fontWeight: "500",
        },
      });
      return;
    }

    // Validate password strength
    const passwordValidation = authService.validatePassword(password);
    if (!passwordValidation.isValid) {
      toast.error(
        passwordValidation.message || "Password does not meet requirements.",
        {
          duration: 5000,
          position: "top-right",
          style: {
            background: "#dc2626",
            color: "#fff",
            fontWeight: "500",
          },
        },
      );
      return;
    }

    // Validate passwords match
    if (password !== confirmPassword) {
      toast.error(
        "The passwords you entered do not match. Please make sure both password fields contain the same password.",
        {
          duration: 5000,
          position: "top-right",
          style: {
            background: "#dc2626",
            color: "#fff",
            fontWeight: "500",
          },
        },
      );
      return;
    }

    const startTime = Date.now();
    const minDisplayTime = 5000; // 5 seconds minimum

    try {
      // Collect device fingerprint for duplicate detection
      // Note: Collected for future API integration when calling Lambda endpoints
      void collectDeviceFingerprint();

      // Register user using auth context (device fingerprint will be sent if API supports it)
      const result = await register(email, password, firstName, lastName);

      // Handle different registration scenarios
      if (result.redirectToLogin) {
        // User exists and is verified - show success and navigate to dashboard
        toast.success(
          "Welcome back! You're already registered and verified. Logging you in...",
          {
            duration: 5000,
            position: "top-right",
            style: {
              background: "#10b981",
              color: "#fff",
              fontWeight: "500",
            },
          },
        );

        // Navigate to dashboard (user is already logged in)
        navigate("/dashboard");
        return;
      }

      // Check if verification is needed
      if (result.needsVerification) {
        const message = result.userExists
          ? "Account already exists but needs verification. Please check your email for a new verification code."
          : "Registration successful! Please check your email for a verification code.";

        toast.success(message, {
          duration: 5000,
          position: "top-right",
          style: {
            background: "#10b981",
            color: "#fff",
            fontWeight: "500",
          },
        });
        return; // Don't navigate, show verification form
      }

      // Registration successful without verification - show success message and navigate to dashboard
      toast.success("Account created successfully! Welcome to Kairos!", {
        duration: 5000,
        position: "top-right",
        style: {
          background: "#10b981",
          color: "#fff",
          fontWeight: "500",
        },
      });

      // Navigate to dashboard
      navigate("/dashboard");
    } catch (error) {
      let errorMessage = "Registration failed. Please try again.";
      let errorCode = "";

      // Handle duplicate detection errors
      if (error instanceof Error) {
        const errorAny = error as any;
        errorMessage = error.message;

        // Check for duplicate detection blocking
        if (
          errorAny.errorCode === "DUPLICATE_DETECTED" ||
          errorAny.response?.data?.error_code === "DUPLICATE_DETECTED"
        ) {
          const attemptId =
            errorAny.attemptId || errorAny.response?.data?.attemptId;
          // const riskScore = errorAny.riskScore || errorAny.response?.data?.riskScore;
          // const flags = errorAny.flags || errorAny.response?.data?.flags || [];

          errorMessage =
            "Registration requires review. Please contact support or wait for approval.";
          if (attemptId) {
            errorMessage += ` (Reference: ${attemptId.substring(0, 8)})`;
          }

          toast.error(errorMessage, {
            duration: 10000,
            position: "top-right",
            style: {
              background: "#dc2626",
              color: "#fff",
              fontWeight: "500",
            },
          });

          // Registration blocked by duplicate detection
          return; // Don't show generic error
        }

        if (errorAny.errorCode) {
          errorCode = ` (Error Code: ${errorAny.errorCode})`;
        }
      }

      toast.error(`${errorMessage}${errorCode}`, {
        duration: 5000,
        position: "top-right",
        style: {
          background: "#dc2626",
          color: "#fff",
          fontWeight: "500",
        },
      });
    } finally {
      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(0, minDisplayTime - elapsedTime);

      setTimeout(() => {
        onLoadingChange?.(false);
      }, remainingTime);
    }
  };

  return (
    <div className="auth-container">
      {showHeader && (
        <div className="auth-header">
          <div className="logo-container cognixis-logo">
            <div className="logo-icon">C</div>
            <span>COGNIXIS</span>
          </div>

          <div className="auth-tabs">
            <div className="auth-tab">Sign In</div>
            <div className="auth-tab active">Create Account</div>
          </div>

          <div className="logo-container kairos-logo">
            <div className="logo-icon">K</div>
            <span>KAIROS</span>
          </div>
        </div>
      )}

      {showHeader && (
        <h1
          style={{
            position: "absolute",
            left: "-9999px",
            width: "1px",
            height: "1px",
            overflow: "hidden",
          }}
        >
          KAIROS
        </h1>
      )}

      {fromExtension && (
        <div
          style={{
            margin: "20px auto",
            maxWidth: "500px",
            padding: "16px",
            backgroundColor: "#e3f2fd",
            border: "1px solid #1976d2",
            borderRadius: "8px",
            fontSize: "14px",
            color: "#1565c0",
            textAlign: "center",
          }}
        >
          <strong>Welcome from the Extension!</strong>
          <p style={{ margin: "8px 0 0 0" }}>
            You've been redirected here to create your account. After
            registration, you can return to the extension and log in.
          </p>
        </div>
      )}

      <div className="auth-form-container">
        {needsVerification && pendingRegistration ? (
          <VerificationForm email={pendingRegistration.email} />
        ) : (
	          <form className="auth-form" onSubmit={handleSubmit}>
	            <div className="form-group">
	              <label htmlFor="email">Email</label>
	              <input
	                ref={emailRef}
                type="email"
                id="email"
	                name="email"
	                value={email}
	                onChange={(e) => setEmail(e.target.value)}
	                placeholder="Enter your email"
	                autoComplete="section-credentials username"
	                required
	              />
	            </div>

	            <div className="form-row">
              <div className="form-group">
                <label htmlFor="password">Password</label>
                <input
                  ref={passwordRef}
                  type="password"
                  id="password"
	                  name="password"
	                  value={password}
	                  onChange={(e) => setPassword(e.target.value)}
	                  placeholder="Create a password"
	                  autoComplete="section-credentials new-password"
	                  minLength={8}
	                  required
	                />
	              </div>

              <div className="form-group">
                <label htmlFor="confirmPassword">Confirm Password</label>
                <input
                  ref={confirmPasswordRef}
                  type="password"
                  id="confirmPassword"
	                  name="confirmPassword"
	                  value={confirmPassword}
	                  onChange={(e) => setConfirmPassword(e.target.value)}
	                  placeholder="Confirm your password"
	                  autoComplete="section-credentials new-password"
	                  minLength={8}
	                  required
	                />
	              </div>
	            </div>

	            <div className="form-row">
	              <div className="form-group">
	                <label htmlFor="firstName">First Name</label>
	                <input
	                  ref={firstNameRef}
	                  type="text"
	                  id="firstName"
	                  name="firstName"
	                  value={firstName}
	                  onChange={(e) => setFirstName(e.target.value)}
	                  placeholder="Enter your first name"
	                  autoComplete="section-profile given-name"
	                  required
	                />
	              </div>

	              <div className="form-group">
	                <label htmlFor="lastName">Last Name</label>
	                <input
	                  ref={lastNameRef}
	                  type="text"
	                  id="lastName"
	                  name="lastName"
	                  value={lastName}
	                  onChange={(e) => setLastName(e.target.value)}
	                  placeholder="Enter your last name"
	                  autoComplete="section-profile family-name"
	                  required
	                />
	              </div>
	            </div>

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="stripeConsent"
                  checked={stripeConsent}
                  onChange={(e) => setStripeConsent(e.target.checked)}
                  required
                />
                <span>
                  I agree that a Stripe account will be created for billing
                  purposes. By registering, you agree to our{" "}
                  <a
                    href="/terms-of-service"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="link"
                  >
                    Terms of Service
                  </a>{" "}
                  and{" "}
                  <a
                    href="/privacy-policy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="link"
                  >
                    Privacy Policy
                  </a>
                  . A Stripe customer account will be created to manage your
                  subscription. Your email and name are shared with Stripe for
                  account management. See{" "}
                  <a
                    href="https://stripe.com/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="link"
                  >
                    Stripe's Privacy Policy
                  </a>
                  .
                </span>
              </label>
            </div>

            <button
              type="submit"
              className="auth-button"
              disabled={
                !firstName ||
                !lastName ||
                !email ||
                !password ||
                !confirmPassword ||
                password !== confirmPassword ||
                !stripeConsent
              }
            >
              Create Account
            </button>
          </form>
        )}
      </div>
    </div>
  );
};
